import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

// Thermal receipt CSS — sizes to 58mm or 80mm rolls. Print with browser Ctrl-P.
function ThermalShell({ width, children }) {
  return (
    <>
      <style>{`
        @page { size: ${width}mm auto; margin: 2mm; }
        html, body { margin: 0; padding: 0; background: white; }
        .thermal {
          width: ${width - 4}mm;
          font-family: 'Courier New', 'IBM Plex Mono', monospace;
          font-size: ${width === 58 ? 10 : 11}px;
          line-height: 1.35;
          color: #000;
          padding: 4mm 2mm;
        }
        .thermal h1 { font-size: ${width === 58 ? 13 : 16}px; margin: 0 0 4px; text-align: center; font-weight: 900; }
        .thermal h2 { font-size: ${width === 58 ? 11 : 13}px; margin: 4px 0; text-align: center; font-weight: 700; }
        .thermal .sep { border-top: 1px dashed #000; margin: 4px 0; }
        .thermal .row { display: flex; justify-content: space-between; }
        .thermal .row.right { justify-content: flex-end; }
        .thermal table { width: 100%; border-collapse: collapse; margin: 4px 0; }
        .thermal th, .thermal td { text-align: left; padding: 1px 0; vertical-align: top; }
        .thermal th { border-bottom: 1px solid #000; font-size: ${width === 58 ? 9 : 10}px; }
        .thermal td.qty { text-align: right; width: 8mm; }
        .thermal td.amt { text-align: right; width: ${width === 58 ? 14 : 18}mm; font-variant-numeric: tabular-nums; }
        .thermal .center { text-align: center; }
        .thermal .big { font-size: ${width === 58 ? 14 : 17}px; font-weight: 900; }
        .thermal .small { font-size: ${width === 58 ? 8 : 9}px; }
        @media print {
          body { background: white; }
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="thermal">{children}</div>
    </>
  );
}

export function KotPrint() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [property, setProperty] = useState(null);

  useEffect(() => {
    api.get("/pos/orders/one/" + orderId).then((r) => {
      setOrder(r.data);
      api.get("/properties").then((rp) => {
        setProperty((rp.data || []).find((p) => p.id === r.data.property_id));
      }).catch(() => {});
    }).catch(() => {});
    api.get("/settings/platform").then((r) => setPlatform(r.data)).catch(() => {});
  }, [orderId]);

  useEffect(() => {
    if (order && platform) setTimeout(() => window.print(), 350);
  }, [order, platform]);

  if (!order || !platform) return <div style={{ padding: 20, fontFamily: "monospace" }}>Loading KOT…</div>;
  const width = Number(platform.printer_paper_width_mm) === 58 ? 58 : 80;
  const time = new Date(order.created_at);

  return (
    <ThermalShell width={width}>
      <h1>*** KOT ***</h1>
      <h2>{property?.name || platform.group_name || ""}</h2>
      <div className="row"><span>KOT #</span><span>{order.order_no}</span></div>
      <div className="row"><span>Time</span><span>{time.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span></div>
      {order.table_order_id ? (
        <div className="row"><span>Table</span><span className="big">{order.table_no || "—"}</span></div>
      ) : (
        <div className="row"><span>Type</span><span>{(order.order_type || "").replace("_", " ").toUpperCase()}</span></div>
      )}
      {order.customer_name && <div className="row"><span>Customer</span><span>{order.customer_name}</span></div>}
      {order.room_number && (
        <div className="row"><span>Room</span><span className="big" data-testid="kot-room-number">{order.room_number}</span></div>
      )}
      <div className="sep" />
      <table>
        <thead><tr><th>Qty</th><th>Item</th></tr></thead>
        <tbody>
          {(order.items || []).map((it, i) => (
            <tr key={i}><td className="qty big">{it.qty}×</td><td className="big">{it.name}</td></tr>
          ))}
        </tbody>
      </table>
      {order.notes && (<><div className="sep" /><div>Notes: {order.notes}</div></>)}
      <div className="sep" />
      <p className="center small">Fired {time.toLocaleString("en-IN")}</p>
      <div className="no-print" style={{ textAlign: "center", marginTop: 10 }}>
        <button onClick={() => window.print()} style={{ padding: "6px 12px" }}>Print</button>
      </div>
    </ThermalShell>
  );
}

export function ReceiptPrint() {
  const { entityId } = useParams(); // table_order_id
  const [detail, setDetail] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [property, setProperty] = useState(null);

  useEffect(() => {
    api.get(`/tables/${entityId}/detail`).then((r) => {
      setDetail(r.data);
      api.get("/properties").then((rp) => {
        setProperty((rp.data || []).find((p) => p.id === r.data.property_id));
      }).catch(() => {});
    }).catch(() => {});
    api.get("/settings/platform").then((r) => setPlatform(r.data)).catch(() => {});
  }, [entityId]);

  useEffect(() => {
    if (detail && platform) setTimeout(() => window.print(), 400);
  }, [detail, platform]);

  if (!detail || !platform) return <div style={{ padding: 20, fontFamily: "monospace" }}>Loading receipt…</div>;
  const width = Number(platform.printer_paper_width_mm) === 58 ? 58 : 80;
  const items = (detail.rounds || []).flatMap((r) => (r.items || []).map((it) => ({ ...it, order_no: r.order_no })));
  const subtotal = detail.final_subtotal ?? detail.running_subtotal ?? 0;
  const tax = detail.final_tax ?? detail.running_tax ?? 0;
  const discount = detail.final_discount ?? 0;
  const total = detail.final_total ?? detail.running_total ?? 0;

  return (
    <ThermalShell width={width}>
      <h1>{property?.name || platform.group_name || "Restaurant"}</h1>
      {property?.address && <p className="center small">{property.address}</p>}
      {platform.gst_number && <p className="center small">GSTIN: {platform.gst_number}</p>}
      {platform.printer_header_text && <p className="center small">{platform.printer_header_text}</p>}
      <div className="sep" />
      <div className="row"><span>Bill</span><span>#{detail.id.slice(-8).toUpperCase()}</span></div>
      <div className="row"><span>Table</span><span className="big">{detail.table_number}</span></div>
      <div className="row"><span>Section</span><span>{detail.section}</span></div>
      {detail.customer_name && <div className="row"><span>Guest</span><span>{detail.customer_name}</span></div>}
      <div className="row"><span>Guests</span><span>{detail.guest_count}</span></div>
      <div className="row"><span>Opened</span><span>{new Date(detail.opened_at).toLocaleString("en-IN")}</span></div>
      {detail.closed_at && <div className="row"><span>Closed</span><span>{new Date(detail.closed_at).toLocaleString("en-IN")}</span></div>}
      {(detail.final_payment_mode === "room_charge" || detail.final_posted_room_number) && (
        <div className="row"><span>Room</span><span className="big" data-testid="pos-receipt-room-number">{detail.final_posted_room_number || detail.final_posted_room || "—"}</span></div>
      )}
      <div className="sep" />
      <table>
        <thead><tr><th style={{ width: "50%" }}>Item</th><th className="qty">Qty</th><th className="amt">Amt</th></tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i}>
              <td>{it.name}<div className="small" style={{ opacity: 0.6 }}>@ {inr(it.price)} · {it.gst_rate}%</div></td>
              <td className="qty">{it.qty}</td>
              <td className="amt">{inr(it.line_subtotal || it.price * it.qty)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sep" />
      <div className="row"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
      <div className="row"><span>GST</span><span>{inr(tax)}</span></div>
      {discount > 0 && <div className="row"><span>Discount</span><span>- {inr(discount)}</span></div>}
      <div className="sep" />
      <div className="row big"><span>TOTAL</span><span>{inr(total)}</span></div>
      {detail.final_payment_mode === "room_charge" ? (
        <p className="center" data-testid="pos-receipt-posted-to-room"><strong>POSTED TO ROOM {detail.final_posted_room_number || "—"}</strong></p>
      ) : detail.final_payment_mode && <p className="center small">Paid via {detail.final_payment_mode.toUpperCase()}</p>}
      <div className="sep" />
      <p className="center small">{platform.printer_footer_text || "Thank you"}</p>
      <div className="no-print" style={{ textAlign: "center", marginTop: 10 }}>
        <button onClick={() => window.print()} style={{ padding: "6px 12px" }}>Print</button>
      </div>
    </ThermalShell>
  );
}
